<script lang="ts">
  /**
   * The bar over a board: the one way into the game's menu on the left, the
   * game's own read-out in the middle (`hud`), its small facts on the right
   * (`trailing`: a coin balance, a level chip), and an optional full-width
   * second row (`strip`: TwinTen's numbers-finished digits, a duel's meters).
   *
   * Everything a player might want mid-game beyond that lives in the menu
   * the button opens, never as more buttons here (TwinTen ADR 0052). The
   * bar pads the top by the notch: it is the first thing on a full-screen
   * view. `GameFrame` places it; a game may also render it on its own.
   *
   * `leading` (since 1.7.0): a slot left of the menu button, for one more
   * way out the game keeps in reach (Home, as an `IconButton` named after
   * where it goes). One, not a toolbar: the rest belongs in the menu.
   *
   * `menuGlyph` (since 1.8.0): `menu` (three lines, the default) or `close`
   * (a cross), for a screen whose one way out leaves it rather than opening
   * a menu (a lesson, a quiz). The button stays the same button in the same
   * place, `onmenu` is still what it does; its accessible name is
   * `menuLabel`, which then defaults to the shell's `close` ("Close"): name
   * where it goes ("Leave the lesson").
   *
   * Test ids: `testid` (default `ui-game-top-bar`) on the bar, `menuTestid`
   * (default `ui-game-menu`) on the button, with `data-glyph`.
   */
  import type { Snippet } from 'svelte';

  import { shellText } from '../i18n.svelte.js';
  import Icon from './Icon.svelte';

  interface Props {
    readonly onmenu: () => void;
    /** The menu button's name. Default the shell's `gameMenu` ("Game menu"). */
    readonly menuLabel?: string | undefined;
    /** The centre: the game's own read-out (stage and score, lines and time). */
    readonly hud?: Snippet | undefined;
    /** The right: small facts (a coin balance, a level chip). */
    readonly trailing?: Snippet | undefined;
    /** A second, full-width row under the first. */
    readonly strip?: Snippet | undefined;
    readonly testid?: string | undefined;
    readonly menuTestid?: string | undefined;
    // ── 1.7.0 ──
    /** Left of the menu button: one more way the game keeps in reach (Home). */
    readonly leading?: Snippet | undefined;
    // ── 1.8.0 ──
    /** The button's glyph: the menu's three lines, or a cross (name it with `menuLabel`). */
    readonly menuGlyph?: 'menu' | 'close';
  }
  const {
    onmenu,
    menuLabel,
    hud,
    trailing,
    strip,
    testid = 'ui-game-top-bar',
    menuTestid = 'ui-game-menu',
    leading,
    menuGlyph = 'menu',
  }: Props = $props();

  const menuName = $derived(menuLabel ?? shellText(menuGlyph === 'close' ? 'close' : 'gameMenu'));
</script>

<header class="ui-game-top" data-testid={testid}>
  <div class="ui-game-top-row">
    {#if leading !== undefined}
      <div class="ui-game-top-leading">{@render leading()}</div>
    {/if}
    <button
      type="button"
      class="ui-game-top-menu"
      aria-label={menuName}
      data-testid={menuTestid}
      data-glyph={menuGlyph}
      onclick={onmenu}
    >
      <Icon name={menuGlyph} size={22} color="var(--ui-accent)" />
    </button>
    <div class="ui-game-top-hud">
      {#if hud !== undefined}{@render hud()}{/if}
    </div>
    <div class="ui-game-top-trailing">
      {#if trailing !== undefined}{@render trailing()}{/if}
    </div>
  </div>
  {#if strip !== undefined}
    <div class="ui-game-top-strip">{@render strip()}</div>
  {/if}
</header>

<style>
  .ui-game-top {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    padding: calc(0.4rem + env(safe-area-inset-top)) 0.6rem 0.3rem;
    background: var(--ui-ground);
    color: var(--ui-ink);
    font-family: var(--ui-font);
  }

  .ui-game-top-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .ui-game-top-menu {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
  }

  .ui-game-top-menu:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 1px;
  }

  .ui-game-top-leading {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
  }

  .ui-game-top-hud {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    line-height: 1.2;
  }

  .ui-game-top-trailing {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }

  .ui-game-top-strip {
    display: flex;
    flex-direction: column;
  }
</style>
